import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Store, Package, Images, Clock, Wrench, Megaphone,
  Inbox, Star, BarChart3, Globe, Receipt, Users, LifeBuoy, Shield,
  Plus, LogOut, Menu, X, ChevronDown, Check, Building2, MapPin, Layers, Gauge, Sparkles, ShieldAlert, ShieldCheck,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import SupportBanner from "@/components/admin/SupportBanner";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useTranslation } from "react-i18next";
import { GoldLoading, GoldError } from "@/components/admin/GoldUi";
import "@/styles/fundio-gold-master.css";

const ROLE_KEY = { owner: "roles.owner", admin: "roles.admin", editor: "roles.editor", employee: "roles.employee" };

const NAV = [
  { key: "dashboard", labelKey: "nav.dashboard", icon: LayoutDashboard, to: "/dashboard" },
  { key: "mein-brocki", labelKey: "nav.mein_brocki", icon: Store, to: "/dashboard/mein-brocki" },
  { key: "fundstuecke", labelKey: "nav.fundstuecke", icon: Package, to: "/dashboard/fundstuecke" },
  { key: "galerie", labelKey: "nav.galerie", icon: Images, to: "/dashboard/galerie", feature: "gallery" },
  { key: "optimierung", labelKey: "nav.optimierung", icon: Sparkles, to: "/dashboard/optimierung", feature: "gallery" },
  { key: "oeffnungszeiten", labelKey: "nav.oeffnungszeiten", icon: Clock, to: "/dashboard/oeffnungszeiten" },
  { key: "services", labelKey: "nav.services", icon: Wrench, to: "/dashboard/services", feature: "services" },
  { key: "aktionen", labelKey: "nav.aktionen", icon: Megaphone, to: "/dashboard/aktionen", feature: "news" },
  { key: "anfragen", labelKey: "nav.anfragen", icon: Inbox, to: "/dashboard/anfragen", badge: true },
  { key: "bewertungen", labelKey: "nav.bewertungen", icon: Star, to: "/dashboard/bewertungen", feature: "reviews" },
  { key: "statistiken", labelKey: "nav.statistiken", icon: BarChart3, soon: true, feature: "statistics" },
  { key: "website", labelKey: "nav.website", icon: Globe, soon: true, feature: "website_preview" },
  { key: "plan", labelKey: "nav.plan", icon: Gauge, to: "/dashboard/plan" },
  { key: "abo", labelKey: "nav.abo", icon: Receipt, to: "/dashboard/abo" },
  { key: "team", labelKey: "nav.team", icon: Users, to: "/dashboard/benutzer" },
  { key: "support", labelKey: "nav.support", icon: LifeBuoy, soon: true },
  { key: "sicherheit", labelKey: "nav.sicherheit", icon: Shield, to: "/dashboard/sicherheit" },
];

// server-side plans authoritatively grant features; this only maps a feature -> the
// lowest plan label to display on the "upgrade" badge.
const MIN_PLAN = {
  mini_website: "LIGHT", items_public: "LIGHT", gallery: "LIGHT", services: "LIGHT",
  news: "LIGHT", reviews: "LIGHT", statistics: "LIGHT", website_preview: "LIGHT",
  subdomain: "LIGHT", custom_domain: "WEBPRO",
};

const ITEM_BASE = "fundio-nav-item font-medium transition-colors";
const NAV_GROUPS = [
  { keys: ["dashboard", "fundstuecke", "anfragen", "mein-brocki"] },
  { labelKey: "goldMaster.groupPresence", keys: ["galerie", "optimierung", "oeffnungszeiten", "services", "aktionen", "bewertungen"] },
  { labelKey: "goldMaster.groupOrganization", keys: ["plan", "abo", "team", "sicherheit"] },
  { keys: ["statistiken", "website", "support"] },
];

function NavList({ active, inquiriesNew = 0, onNavigate, features = null }) {
  const { t } = useTranslation();
  return (
    <nav aria-label={t("goldMaster.navigation")}>
      {NAV_GROUPS.map((group, index) => <div key={index} className="fundio-nav-group">
      {group.labelKey && <p className="fundio-nav-heading">{t(group.labelKey)}</p>}
      {group.keys.map((key) => NAV.find((item) => item.key === key)).map((item) => {
        const Icon = item.icon;
        const showBadge = item.badge && inquiriesNew > 0;
        // three mutually-exclusive states, precedence: not-implemented > upgrade > available
        const gatedOut = item.feature && features && features[item.feature] === false;
        if (item.soon) {
          return (
            <div key={item.key} aria-disabled="true" data-testid={`cockpit-nav-${item.key}`} title={t("nav.soon_title")}
              className={`${ITEM_BASE} cursor-default text-fundio-faint`}>
              <Icon className="h-[17px] w-[17px] shrink-0" />
              <span className="flex-1">{t(item.labelKey)}</span>
              <span className="rounded border border-fundio-line px-2 py-0.5 text-sm font-semibold">{t("nav.soon")}</span>
            </div>
          );
        }
        if (gatedOut) {
          return (
            <div key={item.key} aria-disabled="true" data-testid={`cockpit-nav-${item.key}`} title={t("nav.upgrade_title")}
              className={`${ITEM_BASE} cursor-default text-fundio-faint`}>
              <Icon className="h-[17px] w-[17px] shrink-0" />
              <span className="flex-1">{t(item.labelKey)}</span>
              <span className="rounded bg-fundio-gold-bright/15 px-2 py-0.5 text-sm font-bold text-fundio-gold-bright" data-testid={`cockpit-upgrade-${item.key}`}>{MIN_PLAN[item.feature] || "Upgrade"}</span>
            </div>
          );
        }
        const isActive = item.key === active || (active === "neu" && item.key === "fundstuecke");
        return (
          <Link key={item.key} to={item.to} onClick={onNavigate} aria-current={isActive ? "page" : undefined} data-testid={`cockpit-nav-${item.key}`}
            className={`${ITEM_BASE} ${isActive ? "bg-fundio-gold-bright/15 text-fundio-gold-bright" : "text-fundio-mist hover:bg-white/[0.06] hover:text-fundio-cream"}`}>
            <Icon className="h-[17px] w-[17px] shrink-0" />
            <span className="flex-1">{t(item.labelKey)}</span>
            {showBadge && (
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-fundio-gold-bright px-1 text-[11px] font-bold text-fundio-black">{inquiriesNew}</span>
            )}
          </Link>
        );
      })}</div>)}
    </nav>
  );
}

function SidebarInner({ active, inquiriesNew, onNavigate, features, isPlatformAdmin = false, children }) {
  const { t } = useTranslation();
  return (
    <div className="fundio-sidebar-inner flex h-full flex-col">
      <div className="px-6 py-5">
        <Logo light className="!h-[36px]" />
        <p className="mt-2 text-sm font-semibold tracking-wide text-fundio-mist">{t("nav.cockpit")}</p>
      </div>
      <div className="no-scrollbar flex-1 overflow-y-auto px-3 pb-6">
        <NavList active={active} inquiriesNew={inquiriesNew} onNavigate={onNavigate} features={features} />
        {isPlatformAdmin && (
          <div className="mt-4 border-t border-white/10 pt-3">
            <p className="fundio-nav-heading">{t("nav.system_admin")}</p>
            <Link to="/dashboard/super-admin" onClick={onNavigate} data-testid="cockpit-nav-super-admin"
              className={`${ITEM_BASE} ${active === "super-admin" ? "bg-fundio-gold-bright/15 text-fundio-gold-bright" : "text-fundio-mist hover:bg-white/[0.06] hover:text-fundio-cream"}`}>
              <ShieldAlert className="h-[17px] w-[17px] shrink-0" />
              <span className="flex-1">{t("nav.migration")}</span>
            </Link>
            <Link to="/dashboard/plattform-konten" onClick={onNavigate} data-testid="cockpit-nav-plattform-konten"
              className={`${ITEM_BASE} ${active === "plattform-konten" ? "bg-fundio-gold-bright/15 text-fundio-gold-bright" : "text-fundio-mist hover:bg-white/[0.06] hover:text-fundio-cream"}`}>
              <ShieldCheck className="h-[17px] w-[17px] shrink-0" />
              <span className="flex-1">Plattform-Konten</span>
            </Link>
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

export default function CockpitLayout({ children, active = "dashboard", inquiriesNew = 0, title = "Dashboard", connected = false }) {
  const { logout, user } = useAuth();
  const { t } = useTranslation();
  const cockpitRole = (role) => t(ROLE_KEY[role] || "roles.employee");
  const navigate = useNavigate();
  const [drawer, setDrawer] = useState(false);
  const drawerRef = useRef(null);
  const menuRef = useRef(null);
  const [org, setOrg] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [orgState, setOrgState] = useState("loading"); // loading | ok | needs_selection | no_access
  const [switcher, setSwitcher] = useState(false);
  const [locCtx, setLocCtx] = useState(null);   // MULTI location context
  const [locMenu, setLocMenu] = useState(false);
  const [trial, setTrial] = useState(null);
  const [scopeError, setScopeError] = useState(false);
  const [scopeBusy, setScopeBusy] = useState(false);
  const doLogout = () => { logout(); navigate("/login"); };

  useEffect(() => {
    if (!drawer) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    drawerRef.current?.querySelector("button")?.focus();
    const onKey = (event) => {
      if (event.key === "Escape") { setDrawer(false); return; }
      if (event.key !== "Tab") return;
      const controls = Array.from(drawerRef.current?.querySelectorAll('a[href], button:not([disabled]), input, select, textarea') || []).filter((el) => el.getClientRects().length);
      const first = controls[0], last = controls[controls.length - 1];
      if (!first) { event.preventDefault(); return; }
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = previousOverflow; document.removeEventListener("keydown", onKey); menuRef.current?.focus(); };
  }, [drawer]);

  useEffect(() => {
    let alive = true;
    api.get("/org/current").then(({ data }) => {
      if (!alive) return;
      setOrgState(["ok", "needs_selection", "no_access"].includes(data.status) ? data.status : "error");
      setMemberships(data.memberships || []);
      if (data.status === "ok") {
        setOrg(data.tenant);
        if (data.tenant.account_mode === "multi") {
          api.get("/me/locations").then(({ data: lc }) => { if (alive) setLocCtx(lc); }).catch(() => {});
        }
      }
    }).catch(() => { if (alive) setOrgState("error"); });
    api.get("/usage").then(({ data }) => { if (alive && data.trial?.active) setTrial(data.trial); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const selectTenant = async (tid) => {
    setScopeError(false); setScopeBusy(true);
    try { await api.post("/session/select-tenant", { tenant_id: tid }); window.location.reload(); }
    catch { setScopeError(true); }
    finally { setScopeBusy(false); }
  };

  const selectLocation = async (locId) => {
    setScopeError(false); setScopeBusy(true);
    try { await api.post("/session/select-location", { location_id: locId }); window.location.reload(); }
    catch { setScopeError(true); }
    finally { setScopeBusy(false); }
  };

  const multi = memberships.length > 1;
  const isMultiLoc = !!locCtx?.multi;
  const activeLoc = locCtx?.locations?.find((l) => l.id === locCtx?.active_location_id) || null;
  const effLoc = activeLoc || (locCtx && !locCtx.can_view_all ? locCtx.locations?.[0] : null);
  const locLabel = locCtx?.active_location_id
    ? (activeLoc?.name || t("common.location"))
    : (locCtx?.can_view_all ? t("common.all_locations") : (effLoc?.name || t("common.location")));
  const companyName = org?.company_name || "…";
  const roleLine = org ? `${cockpitRole(org.role)} · ${org.plan}` : "";
  const initials = (companyName || "?").replace(/[^A-Za-zÀ-ÿ ]/g, "").split(" ").filter(Boolean).map((p) => p[0]).slice(0, 2).join("").toUpperCase() || "BR";

  return (
    <div className="fundio-cockpit flex">
      <a href="#fundio-workspace" className="fundio-skip">{t("goldMaster.skipContent")}</a>
      {/* desktop sidebar */}
      <aside className="fundio-sidebar hidden shrink-0 flex-col lg:flex" data-testid="cockpit-sidebar">
        <SidebarInner active={active} inquiriesNew={inquiriesNew} onNavigate={() => {}} features={org?.features} isPlatformAdmin={!!user?.is_platform_admin} />
      </aside>

      {/* mobile drawer */}
      {drawer && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="cockpit-drawer" role="dialog" aria-modal="true" aria-label={t("goldMaster.navigation")} ref={drawerRef}>
          <div className="absolute inset-0 bg-black/60" onClick={() => setDrawer(false)} />
          <div className="absolute inset-y-0 left-0 w-72">
            <SidebarInner active={active} inquiriesNew={inquiriesNew} onNavigate={() => setDrawer(false)} features={org?.features} isPlatformAdmin={!!user?.is_platform_admin}>
              <div className="mt-5 space-y-3 border-t border-fundio-line px-3 pt-4 text-sm text-fundio-cream">
                <p className="font-bold">{companyName}</p><p>{roleLine}</p>
                {multi && <div data-testid="mobile-brocki-switcher"><p className="mb-2">{t("nav.switch_brocki")}</p>{memberships.map((m) => <button type="button" key={m.tenant_id} disabled={scopeBusy} data-testid={`mobile-brocki-option-${m.tenant_id}`} onClick={() => selectTenant(m.tenant_id)} className="fundio-action fundio-action--full mb-2">{m.company_name}{org?.id === m.tenant_id && <Check size={18} aria-hidden="true" />}</button>)}</div>}
                {isMultiLoc && <div data-testid="mobile-location-switcher"><p className="mb-2">{t("nav.choose_location")} · {locLabel}</p>{locCtx?.can_view_all && <button type="button" disabled={scopeBusy} data-testid="mobile-location-option-all" onClick={() => selectLocation(null)} className="fundio-action fundio-action--full mb-2">{t("common.all_locations")}</button>}{(locCtx?.locations || []).map((l) => <button type="button" key={l.id} disabled={scopeBusy} data-testid={`mobile-location-option-${l.id}`} onClick={() => selectLocation(l.id)} className="fundio-action fundio-action--full mb-2">{l.name}{locCtx.active_location_id === l.id && <Check size={18} aria-hidden="true" />}</button>)}</div>}
                {scopeError && <p role="alert">{t("goldMaster.scopeError")}</p>}
                <button type="button" onClick={doLogout} data-testid="cockpit-mobile-logout" className="fundio-action fundio-action--primary fundio-action--full"><LogOut size={18} aria-hidden="true" />{t("common.logout")}</button>
              </div>
            </SidebarInner>
            <button aria-label={t("goldMaster.closeMenu")} onClick={() => setDrawer(false)} className="absolute right-2 top-2 flex h-12 w-12 items-center justify-center rounded-md text-fundio-mist hover:bg-white/10">
              <X size={18} />
            </button>
          </div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col" inert={drawer ? true : undefined}>
        <SupportBanner />
        {/* desktop topbar */}
        <header className="fundio-topbar sticky top-0 z-20 hidden items-center justify-between border-b border-fundio-border bg-white lg:flex" data-testid="cockpit-topbar">
          <span className="text-[15px] font-semibold text-fundio-ink" data-testid="topbar-title">{title}</span>
          <div className="flex items-center gap-4">
            {trial?.active && (
              <Link to="/dashboard/plan" data-testid="trial-pill"
                className={`hidden items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-bold transition-colors sm:flex ${trial.reminder === "hard" ? "bg-red-50 text-red-700 hover:bg-red-100" : trial.reminder === "soft" ? "bg-amber-50 text-amber-700 hover:bg-amber-100" : "bg-fundio-gold/10 text-fundio-gold hover:bg-fundio-gold/20"}`}
                title={trial.ends_at ? t("nav.trial_until", { date: new Date(trial.ends_at).toLocaleDateString("de-CH") }) : ""}>
                <Clock size={13} /> {t(trial.days_left === 1 ? "nav.trial_days_one" : "nav.trial_days_other", { count: trial.days_left })}
              </Link>
            )}
            {isMultiLoc && (
              <div className="relative" data-testid="location-switcher">
                <button type="button" disabled={scopeBusy} aria-expanded={locMenu} onClick={() => setLocMenu((s) => !s)}
                  className="flex items-center gap-2 rounded-lg border border-fundio-gold/40 bg-fundio-gold/[0.06] py-1.5 pl-2.5 pr-3 text-left hover:bg-fundio-gold/10"
                  data-testid="location-switcher-trigger">
                  {(locCtx?.active_location_id || (!locCtx?.can_view_all && effLoc)) ? <MapPin size={15} className="text-fundio-gold" /> : <Layers size={15} className="text-fundio-gold" />}
                  <span className="hidden leading-tight sm:block">
                    <span className="block font-mono text-[9px] uppercase tracking-[0.16em] text-fundio-slate">{t("common.location")}</span>
                    <span className="block max-w-[160px] truncate text-[13px] font-bold text-fundio-ink" data-testid="active-location-label">{locLabel}</span>
                  </span>
                  <ChevronDown size={15} className="text-fundio-slate" />
                </button>
                {locMenu && (
                  <div className="absolute right-0 z-40 mt-2 w-64 overflow-hidden rounded-xl border border-fundio-border bg-white py-1 shadow-lg" data-testid="location-switcher-menu">
                    <p className="px-3 pb-1 pt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-fundio-faint">{t("nav.choose_location")}</p>
                    {locCtx?.can_view_all && (
                      <button onClick={() => selectLocation(null)} data-testid="location-option-all"
                        className="flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-fundio-subtle">
                        <Layers size={16} className="shrink-0 text-fundio-slate" />
                        <span className="flex-1 text-[13px] font-medium text-fundio-ink">{t("common.all_locations")}</span>
                        {!locCtx?.active_location_id && <Check size={15} className="text-fundio-gold" />}
                      </button>
                    )}
                    {(locCtx?.locations || []).map((l) => (
                      <button key={l.id} onClick={() => selectLocation(l.id)} data-testid={`location-option-${l.id}`}
                        className="flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-fundio-subtle">
                        <MapPin size={16} className="shrink-0 text-fundio-slate" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[13px] font-medium text-fundio-ink">{l.name}</span>
                          {l.city && <span className="block text-[11px] text-fundio-slate">{l.city}</span>}
                        </span>
                        {locCtx?.active_location_id === l.id && <Check size={15} className="text-fundio-gold" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {connected && orgState === "ok" && (
              <span className="hidden items-center gap-1.5 rounded-full border border-status-success-border bg-status-success-bg px-3 py-1 text-xs font-medium text-status-success sm:inline-flex" data-testid="topbar-status">
                <span className="h-1.5 w-1.5 rounded-full bg-status-success" /> {t("goldMaster.dataLoaded")}
              </span>
            )}
            <LanguageSwitcher variant="dark" />
            <div className="relative" data-testid="topbar-account">
              <button type="button" disabled={scopeBusy} aria-expanded={multi ? switcher : undefined} onClick={() => multi && setSwitcher((s) => !s)}
                className={`flex items-center gap-2.5 rounded-lg border border-fundio-border bg-white py-1.5 pl-1.5 pr-3 text-left ${multi ? "hover:bg-fundio-subtle" : "cursor-default"}`}
                data-testid="brocki-switcher-trigger">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-fundio-gold text-sm font-bold text-fundio-black">{initials}</span>
                <span className="hidden leading-tight sm:block">
                  <span className="block max-w-[180px] truncate text-[13px] font-semibold text-fundio-ink" data-testid="current-brocki-name">{companyName}</span>
                  <span className="block text-[11px] text-fundio-slate" data-testid="current-brocki-meta">{roleLine}</span>
                </span>
                {multi && <ChevronDown size={15} className="text-fundio-slate" />}
              </button>
              {multi && switcher && (
                <div className="absolute right-0 z-40 mt-2 w-64 overflow-hidden rounded-xl border border-fundio-border bg-white py-1 shadow-lg" data-testid="brocki-switcher-menu">
                  <p className="px-3 pb-1 pt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-fundio-faint">{t("nav.switch_brocki")}</p>
                  {memberships.map((m) => (
                    <button key={m.tenant_id} onClick={() => selectTenant(m.tenant_id)} data-testid={`brocki-option-${m.tenant_id}`}
                      className="flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-fundio-subtle">
                      <Building2 size={16} className="shrink-0 text-fundio-slate" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13px] font-medium text-fundio-ink">{m.company_name}</span>
                        <span className="block text-[11px] text-fundio-slate">{cockpitRole(m.role)} · {m.plan}</span>
                      </span>
                      {org?.id === m.tenant_id && <Check size={15} className="text-fundio-gold" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={doLogout} data-testid="cockpit-logout" title={t("common.logout")} aria-label={t("common.logout")}
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-fundio-border bg-white text-fundio-muted transition-colors hover:bg-fundio-subtle">
              <LogOut size={16} />
            </button>
          </div>
        </header>

        {/* mobile topbar */}
        <header className="fundio-mobile-header sticky top-0 z-20 flex items-center justify-between bg-fundio-dark px-4 py-2 lg:hidden">
          <Logo light className="!h-8" />
          <div className="flex items-center gap-2">
            <LanguageSwitcher variant="light" />
            <button ref={menuRef} aria-label={t("goldMaster.openMenu")} aria-expanded={drawer} onClick={() => setDrawer(true)} data-testid="cockpit-menu-btn" className="flex h-12 w-12 items-center justify-center rounded-md text-white hover:bg-white/10">
              <Menu size={20} />
            </button>
          </div>
        </header>

        <main id="fundio-workspace" tabIndex={-1} className="fundio-workspace flex-1" data-testid="cockpit-workspace">
          {scopeError && <p role="alert" className="fundio-error mb-4">{t("goldMaster.scopeError")}</p>}
          {orgState === "loading" ? <GoldLoading label={t("goldMaster.loading")} /> : orgState === "error" ? <GoldError testid="cockpit-org-error" message={t("goldMaster.organizationError")} retryLabel={t("goldMaster.retry")} onRetry={() => window.location.reload()} /> : orgState === "needs_selection" ? (
            <div className="mx-auto max-w-xl py-8" data-testid="brocki-selector">
              <h1 className="text-[26px] font-black" style={{ color: "#2C2E33" }}>{t("nav.needs_selection_title")}</h1>
              <p className="mt-1.5 text-[14px]" style={{ color: "#5B6472" }}>{t("nav.needs_selection_body")}</p>
              <div className="mt-6 space-y-3">
                {memberships.map((m) => (
                  <button key={m.tenant_id} onClick={() => selectTenant(m.tenant_id)} data-testid={`brocki-select-${m.tenant_id}`}
                    className="flex w-full items-center gap-3.5 rounded-xl border bg-white p-4 text-left transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-md" style={{ borderColor: "#E7E1D6" }}>
                    <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-fundio-gold/10"><Building2 size={20} className="text-fundio-gold" /></span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[15px] font-bold" style={{ color: "#2C2E33" }}>{m.company_name}</span>
                      <span className="block text-[12.5px]" style={{ color: "#777F88" }}>{cockpitRole(m.role)} · {m.plan}</span>
                    </span>
                    <span className="rounded-full bg-fundio-gold px-3 py-1.5 text-[12px] font-bold text-fundio-black">{t("common.open")}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : orgState === "no_access" ? (
            <div className="mx-auto max-w-md py-16 text-center" data-testid="no-access-state">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-fundio-gold/10"><Building2 className="text-fundio-gold" /></span>
              <h1 className="mt-4 text-[22px] font-black" style={{ color: "#2C2E33" }}>{t("nav.no_access_title")}</h1>
              <p className="mt-2 text-[14px]" style={{ color: "#5B6472" }}>{t("nav.no_access_body")}</p>
              <button onClick={doLogout} className="mt-6 rounded-lg border px-4 py-2.5 text-[14px] font-medium" style={{ borderColor: "#E7E1D6", color: "#2C2E33" }}>{t("common.logout")}</button>
            </div>
          ) : (
            children
          )}
        </main>
      </div>

      {/* mobile bottom nav */}
      <nav aria-label={t("goldMaster.quickNavigation")} inert={drawer ? true : undefined} className="fundio-bottom-nav fixed bottom-0 left-0 right-0 z-30 flex items-center border-t border-fundio-line bg-fundio-dark lg:hidden">
        {[
          { key: "dashboard", label: t("goldMaster.overview"), icon: LayoutDashboard, to: "/dashboard" },
          { key: "fundstuecke", label: t("nav.fundstuecke"), icon: Package, to: "/dashboard/fundstuecke" },
        ].map((it) => (
          <Link key={it.key} to={it.to} aria-current={active === it.key ? "page" : undefined} data-testid={`cockpit-bottomnav-${it.key}`}
            className={`flex flex-1 flex-col items-center gap-1 py-2.5 text-[10px] font-medium ${active === it.key ? "text-fundio-gold-bright" : "text-fundio-mist"}`}>
            <it.icon size={20} /> {it.label}
          </Link>
        ))}
        <Link to="/dashboard/neu" aria-current={active === "neu" ? "page" : undefined} data-testid="cockpit-bottomnav-neu">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-fundio-gold text-fundio-black">
            <Plus size={20} />
          </span>
          <span>{t("nav.bottom_new")}</span>
        </Link>
        <Link to="/dashboard/anfragen" aria-current={active === "anfragen" ? "page" : undefined} data-testid="cockpit-bottomnav-anfragen"
          className="relative flex flex-1 flex-col items-center gap-1 py-2.5 text-[10px] font-medium text-fundio-mist">
          <span className="relative">
            <Inbox size={20} />
            {inquiriesNew > 0 && <span className="absolute -right-2 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-fundio-gold-bright px-1 text-[9px] font-bold text-fundio-black">{inquiriesNew}</span>}
          </span>
          {t("nav.anfragen")}
        </Link>
      </nav>
    </div>
  );
}
